import { useMemo } from 'react';
import type { FilterFacet } from './Filter.types';
import { countActiveOptions } from './presentFacets';
import { isComposedFacet } from './Filters.constants';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';

/**
 * The facets that earn a pill. `FACET_ORDER` presents at most four (Style, Colour, Free,
 * Feature) and they all fit beside the search bar from 1024px up, so there is no overflow
 * row: every facet kept here is shown.
 *
 * A one-to-one facet (Style, Colour) needs two options to be worth a pill — with one, every
 * design on the page already has it. A composed facet (Free, Feature) is worth a pill at a
 * single option, because that option still narrows the page: a category whose only feature is
 * Magnet would otherwise offer no way to ask for magnets. A facet also keeps its pill while it
 * holds a selection, so the selection can be cleared where it was made, and while its options
 * are still being counted.
 */
export const useVisibleFilterFacets = (facets: FilterFacet[]) => {
  const { activePrintFilters } = useDesignGalleryFiltersContext();

  return useMemo(() => {
    return facets.filter(facet => {
      if (facet.pending || countActiveOptions(facet, activePrintFilters) > 0) return true;
      const countableOptions = facet.options.filter(option => option.matchingItemCount > 0).length;
      return isComposedFacet(facet.groupCode) ? countableOptions > 0 : countableOptions > 1;
    });
  }, [facets, activePrintFilters]);
};
